import React, { useState, useMemo } from 'react';
import { Notification, Project, MilestoneStatus } from '../types';
import {
    Check,
    MessageSquare,
    Info,
    Bell,
    Clock,
    ArrowRight,
    Archive,
    CheckCircle2,
    Inbox as InboxIcon,
    Filter,
    Search,
    AlertCircle,
    FileText,
    MoreHorizontal
} from 'lucide-react';
import { Button, StatusBadge } from './UIComponents';

interface InboxViewProps {
    notifications: Notification[];
    projects?: Project[];
    onSelect?: (notification: Notification) => void;
    onMarkAsRead?: (notificationId: string) => void;
    onMarkAllAsRead?: () => void;
}

const getTypeStyles = (type: string) => {
    switch (type) {
        case 'approval': return { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500/10 border-emerald-500/20' };
        case 'feedback': return { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-500/10 border-blue-500/20' };
        default: return { icon: Info, color: 'text-zinc-500', bg: 'bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700' };
    }
};

const NotificationItem: React.FC<{
    note: Notification;
    isActionable?: boolean;
    project?: Project;
    onSelect?: (note: Notification) => void;
    onMarkAsRead?: (notificationId: string) => void;
}> = ({ note, isActionable = false, project, onSelect, onMarkAsRead }) => {
    const style = getTypeStyles(note.type);
    const Icon = style.icon;

    return (
        <div
            onClick={() => onSelect && onSelect(note)}
            className={`group relative p-4 rounded-xl border transition-all cursor-pointer mb-3 
              ${isActionable
                    ? 'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 hover:shadow-md hover:border-zinc-300 dark:hover:border-zinc-700'
                    : 'bg-transparent border-transparent hover:bg-zinc-50 dark:hover:bg-zinc-900/50'
                }`}
        >
            <div className="flex gap-4">
                {/* Icon */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isActionable ? style.bg : 'bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800'}`}>
                    <Icon size={18} className={isActionable ? style.color : 'text-zinc-500'} strokeWidth={2} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide flex items-center gap-2">
                            {project?.name || 'Unknown Project'}
                            {isActionable && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />}
                        </span>
                        <span className="text-[11px] text-zinc-400 font-mono">
                            {new Date(note.date).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                        </span>
                    </div>

                    <div className="text-[14px] text-zinc-900 dark:text-zinc-100 font-medium leading-snug mb-1">
                        {note.message}
                    </div>

                    <div className="text-[13px] text-zinc-500 line-clamp-1">
                        {note.type === 'approval' ? 'Milestone requires your attention.' : 'New activity reported on this project.'}
                    </div>

                    {isActionable && (
                        <div className="mt-4 flex gap-2">
                            <Button size="sm" className="h-8 text-[12px] bg-zinc-900 text-white dark:bg-white dark:text-black">
                                View Details
                            </Button>
                            <Button
                                size="sm"
                                variant="secondary"
                                className="h-8 text-[12px]"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onMarkAsRead?.(note.id);
                                }}
                            >
                                Mark as Read
                            </Button>
                        </div>
                    )}
                </div>

                {/* Hover Actions for non-actionable */}
                {!isActionable && (
                    <div className="hidden group-hover:flex flex-col gap-1 items-end justify-center pl-2">
                        <button
                            className="p-2 text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                            title="Mark as read"
                            onClick={(e) => {
                                e.stopPropagation();
                                onMarkAsRead?.(note.id);
                            }}
                        >
                            <div className="w-2 h-2 rounded-full border border-current" />
                        </button>
                        <button
                            className="p-2 text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                            title="Archive"
                            onClick={(e) => { e.stopPropagation(); }}
                        >
                            <Archive size={16} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export const InboxView: React.FC<InboxViewProps> = ({ notifications, projects = [], onSelect, onMarkAsRead, onMarkAllAsRead }) => {
    const [activeFilter, setActiveFilter] = useState<'all' | 'unread' | 'approval'>('all');

    const filteredNotifications = notifications.filter(n => {
        if (activeFilter === 'unread') return !n.isRead;
        if (activeFilter === 'approval') return n.type === 'approval';
        return true;
    });

    // Separate "Requires Action" (Approvals) from "Informational"
    const actionableItems = filteredNotifications.filter(n => n.type === 'approval' && !n.isRead);
    const informationalItems = filteredNotifications.filter(n => !(n.type === 'approval' && !n.isRead));

    // Group informational by date
    const groupedNotifications = useMemo(() => {
        const groups: Record<string, Notification[]> = {};

        informationalItems.forEach(n => {
            const date = new Date(n.date);
            const today = new Date();
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);

            let key = 'Earlier';
            if (date.toDateString() === today.toDateString()) key = 'Today';
            else if (date.toDateString() === yesterday.toDateString()) key = 'Yesterday';

            if (!groups[key]) groups[key] = [];
            groups[key].push(n);
        });

        return groups;
    }, [informationalItems]);

    const getProject = (id: string) => projects.find(p => p.id === id);

    return (
        <div className="w-full h-full bg-white dark:bg-black animate-fade-in overflow-y-auto">
            <div className="max-w-3xl mx-auto px-6 py-10">
                {/* Header */}
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-medium text-zinc-900 dark:text-zinc-100 tracking-tight mb-2">Inbox</h1>
                        <p className="text-zinc-500 text-[13px]">Stay updated on your project activity.</p>
                    </div>
                    <div className="flex gap-2">
                        <button className="p-2 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-lg transition-colors">
                            <Search size={18} />
                        </button>
                        <button className="p-2 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-900 rounded-lg transition-colors">
                            <Archive size={18} />
                        </button>
                    </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex items-center gap-2 mb-10 overflow-x-auto no-scrollbar pb-1">
                    {[
                        { id: 'all', label: 'All Activity' },
                        { id: 'unread', label: 'Unread', count: notifications.filter(n => !n.isRead).length },
                        { id: 'approval', label: 'Approvals' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveFilter(tab.id as any)}
                            className={`
                            px-4 py-2 rounded-full text-[13px] font-medium transition-all whitespace-nowrap flex items-center gap-2
                            ${activeFilter === tab.id
                                    ? 'bg-zinc-900 text-white dark:bg-white dark:text-black shadow-sm'
                                    : 'bg-zinc-100 dark:bg-zinc-900 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-800'
                                }
                        `}
                        >
                            {tab.label}
                            {tab.count !== undefined && tab.count > 0 && (
                                <span className={`
                                px-1.5 py-0.5 rounded-full text-[10px] 
                                ${activeFilter === tab.id
                                        ? 'bg-white/20 text-white dark:bg-black/10 dark:text-black'
                                        : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                                    }
                            `}>
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Actionable Items Section */}
                {actionableItems.length > 0 && (
                    <div className="mb-10 animate-fade-in">
                        <h3 className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                            <AlertCircle size={12} className="text-amber-500" />
                            Requires Action
                        </h3>
                        <div className="space-y-2">
                            {actionableItems.map(note => (
                                <NotificationItem
                                    key={note.id}
                                    note={note}
                                    isActionable={true}
                                    project={getProject(note.projectId)}
                                    onSelect={onSelect}
                                    onMarkAsRead={onMarkAsRead}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Timeline */}
                <div className="space-y-8">
                    {Object.keys(groupedNotifications).length === 0 && actionableItems.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
                            <div className="w-16 h-16 bg-zinc-100 dark:bg-zinc-900 rounded-2xl flex items-center justify-center mb-4">
                                <InboxIcon size={32} strokeWidth={1.5} />
                            </div>
                            <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-1">All caught up</h3>
                            <p className="text-[13px]">No new notifications to show.</p>
                            <Button variant="secondary" className="mt-6">View Archive</Button>
                        </div>
                    ) : (
                        Object.keys(groupedNotifications).map(group => (
                            <div key={group} className="animate-fade-in">
                                <h3 className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-4 sticky top-0 bg-white dark:bg-black py-2 z-10">
                                    {group}
                                </h3>
                                <div className="space-y-1">
                                    {groupedNotifications[group].map(note => (
                                        <NotificationItem
                                            key={note.id}
                                            note={note}
                                            project={getProject(note.projectId)}
                                            onSelect={onSelect}
                                            onMarkAsRead={onMarkAsRead}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};
